import type { ReactNode } from 'react';

/**
 * Canonical chart shell — white surface, 1px `#e5e7eb` border, soft shadow,
 * matching `.standard-plot-container` and the duplicate `ChartBox`
 * components in `features/mars-forecasts/ChartBox.tsx` and
 * `features/mars-portfolio/sectionUi.tsx`.
 *
 * Wraps children in a fixed-height inner div so a Plotly chart inside can
 * fill the available space via `useResizeHandler`.
 */
export interface ChartCardProps {
  /** Height of the inner plot area in pixels. Default `360`. */
  height?: number;
  /** Optional title rendered above the chart. */
  title?: string;
  /** Optional toolbar (selectors, toggles) rendered to the right of the title. */
  toolbar?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function ChartCard({
  height = 360,
  title,
  toolbar,
  children,
  className,
}: ChartCardProps): JSX.Element {
  const classes = [
    'bg-white rounded-md border border-[#e5e7eb] shadow-sm p-0',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const hasHeader = Boolean(title) || Boolean(toolbar);

  return (
    <div className={classes}>
      {hasHeader ? (
        <div className="flex items-center justify-between gap-1.5 mb-1">
          {title ? (
            <h3 className="text-[0.95rem] font-semibold text-[color:var(--text-dark)] m-0">
              {title}
            </h3>
          ) : (
            <span />
          )}
          {toolbar ? <div className="flex items-center gap-1.5">{toolbar}</div> : null}
        </div>
      ) : null}
      <div style={{ height }}>{children}</div>
    </div>
  );
}

export default ChartCard;
